import type {Theme} from "@mui/material";
import styled from "styled-components";

/**
 * Externalized styles for {@link ./ClosableDialog}: the top-right ✕ and the body padding under the title, first
 * written for the transfer row's link dialog (ticket #32) and shared since the live card's QR and game dialogs
 * (kitecorp/transferify-ui#3320) and the success cards' coupon (kitecorp/transferify-ui#3343) took the same layout.
 */

/** Where the close's painted 30px square sits, from the paper's top and right edges. */
const CLOSE_INSET_PX = 12;
/** How far the close's 44px touch target reaches past that square, on each side. */
const CLOSE_TARGET_BLEED_PX = 7;

/**
 * The explicit close (top-right ✕), clear of the title, which keeps 60px on its right for it.
 *
 * The button is the 44px touch target and its `::before` is the 30px square that paints, so the control looks
 * as it always has and a thumb still finds it: it is the only visible way out of the coupon's dialog on a phone,
 * where the hanging ticket's own ✕ was 44px (kitecorp/transferify-ui#3343). The glyph sits above that square.
 */
export const CloseX = styled.button`
	position: absolute;
	top: ${CLOSE_INSET_PX - CLOSE_TARGET_BLEED_PX}px;
	right: ${CLOSE_INSET_PX - CLOSE_TARGET_BLEED_PX}px;
	width: 44px;
	height: 44px;
	padding: 0;
	border: none;
	background: transparent;
	color: ${({theme}) => theme.tokens.ink3};
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: color 0.14s;

	&::before {
		content: "";
		position: absolute;
		inset: ${CLOSE_TARGET_BLEED_PX}px;
		border-radius: 8px;
		transition: background 0.14s;
	}

	svg {
		position: relative;
	}

	&:hover {
		color: ${({theme}) => theme.tokens.ink};
	}

	&:hover::before {
		background: ${({theme}) => theme.tokens.field};
	}

	&:focus-visible {
		outline: none;
	}

	&:focus-visible::before {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: 2px;
	}

	@media (prefers-reduced-motion: reduce) {
		transition: none;

		&::before {
			transition: none;
		}
	}
`;

/** MUI's `xs` paper cap and the margin `Dialog` keeps around its paper, from its own `paperWidthXs` rule. */
const MUI_XS_PAPER_PX = 444;
const MUI_PAPER_MARGIN_PX = 32;
/** The page's own side gutter on a phone, which the success cards keep. */
const PAGE_GUTTER_PX = 16;

/**
 * The `narrowGutter` paper: on a phone, MUI's margin each side becomes the page's gutter, so a 375px screen gives
 * the content 32px more. Only below the width at which MUI starts shrinking the paper under its `xs` cap; above
 * it the paper is already at that cap and nothing changes.
 *
 * An `sx` rather than a `styled(BaseDialog)` wrapper, as `DeleteAccountDialog.styles` explains:
 * `scripts/dialog-focus-ownership-sweep.mjs` identifies every overlay by the tag it is mounted as.
 */
export const narrowGutterSx = (theme: Theme) => ({
	"& .MuiDialog-paper": {
		[theme.breakpoints.down(MUI_XS_PAPER_PX + MUI_PAPER_MARGIN_PX * 2)]: {
			margin: `${PAGE_GUTTER_PX}px`,
			width: `calc(100% - ${PAGE_GUTTER_PX * 2}px)`,
			maxWidth: `calc(100% - ${PAGE_GUTTER_PX * 2}px)`,
		},
	},
});

/** Under the title: the dialog's own horizontal padding, and its bottom padding. */
export const DialogBody = styled.div`
	display: flex;
	flex-direction: column;
	align-items: stretch;
	padding: 0 24px 24px;
`;

/** A centred column for a QR tile and what sits under it. */
export const CentredColumn = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 18px;
	padding-top: 4px;
	min-width: 0;
`;
